import { useState, useEffect, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import BackOfficeAPIClient from '../../../../clients/BackofficeAPIClient'
import { People } from '../../../../types/People'
import { SignatureGroupDataResponse } from '../Types/Interface/SignatureGroupDataResponse'
import { stringifyRelances } from '../Types/Interface/Relance'
import { useCreditsManagement } from './useCreditsManagement'
import { useAlertsManagement } from './useAlertsManagement'
import { useRelancesManagement } from './useRelancesManagement'

export interface GroupConnexionState {
    IDSignatureParametreConnexion?: number
    IDPeopleRepresentant?: number
    Token?: string
    OriginalToken?: string
    IsTokenModified?: boolean
    TestStatus?: 'inactive' | 'active' | 'error'
    EstActif?: boolean
}

export interface SiteOverrideState {
    IDSite: number
    Descr: string
    IDSignatureParametreConnexion?: number
    IDPeopleRepresentant?: number
    Token?: string
    OriginalToken?: string
    IsTokenModified?: boolean
    TestStatus?: 'inactive' | 'active' | 'error'
    EstActif?: boolean
}

export interface CompanyOverrideState {
    IDPlaque: number
    NomSociete: string
    CreditsConsommes: number
    ContreSignatureAutomatique: boolean | null
    sites: SiteOverrideState[]
}

export interface CompanyCreditsBdc {
    noCmd: string
    creditsConsommes: number | null
    loading: boolean
}

export interface UseGroupSignatureFormReturn {
    loading: boolean
    saving: boolean
    hasChanges: boolean
    showCancelModal: boolean
    setShowCancelModal: (v: boolean) => void
    showDeleteModal: boolean
    setShowDeleteModal: (v: boolean) => void
    users: People[]

    groupConnexion: GroupConnexionState
    handleUpdateGroupConnexion: (field: keyof GroupConnexionState, value: any) => void
    handleTestGroupConnexion: () => Promise<void>
    testingGroupConnexion: boolean

    contreSignatureGroupe: boolean
    setContreSignatureGroupe: (v: boolean) => void

    applyConnectionToAll: boolean
    applyCounterSignToAll: boolean
    handleToggleApplyConnectionToAll: () => void
    handleToggleApplyCounterSignToAll: () => void

    companyOverrides: CompanyOverrideState[]
    handleUpdateSiteOverride: (idPlaque: number, idSite: number, field: keyof SiteOverrideState, value: any) => void
    handleTestSiteConnection: (idPlaque: number, idSite: number) => Promise<void>
    testingSiteConnection: string | null
    handleUpdateCompanyCounterSign: (idPlaque: number, value: boolean | null) => void

    creditsByBdc: Record<number, CompanyCreditsBdc>
    handleFilterCreditsByBdc: (idPlaque: number, noCmd: string) => Promise<void>

    creditsHook: ReturnType<typeof useCreditsManagement>
    alertsHook: ReturnType<typeof useAlertsManagement>
    relancesHook: ReturnType<typeof useRelancesManagement>

    handleSave: () => Promise<void>
    handleCancel: () => void
    confirmCancel: () => void
    handleDelete: () => Promise<void>

    getConnectionToggleClass: (status?: 'inactive' | 'active' | 'error') => string
}

export function useGroupSignatureForm(appName: string, history: ReturnType<typeof useHistory>): UseGroupSignatureFormReturn {
    const backofficeApiClient = new BackOfficeAPIClient()

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasChanges, setHasChanges] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [users, setUsers] = useState<People[]>([])

    const [groupConnexion, setGroupConnexion] = useState<GroupConnexionState>({
        EstActif: false,
        TestStatus: 'inactive',
        IsTokenModified: false,
    })
    const [testingGroupConnexion, setTestingGroupConnexion] = useState(false)

    const [contreSignatureGroupe, setContreSignatureGroupeState] = useState(false)

    const [companyOverrides, setCompanyOverrides] = useState<CompanyOverrideState[]>([])
    const [testingSiteConnection, setTestingSiteConnection] = useState<string | null>(null)
    const [applyConnectionToAll, setApplyConnectionToAll] = useState(true)
    const [applyCounterSignToAll, setApplyCounterSignToAll] = useState(true)

    const [creditsByBdc, setCreditsByBdc] = useState<Record<number, CompanyCreditsBdc>>({})

    const creditsHook = useCreditsManagement(setHasChanges)
    const alertsHook = useAlertsManagement(setHasChanges)
    const relancesHook = useRelancesManagement(setHasChanges)

    const fetchData = useCallback(async () => {
        setLoading(true)
        try {
            const [usersData, groupData] = await Promise.all([
                backofficeApiClient.get<People[]>('/interfaces/signatures/utilisateurs'),
                (backofficeApiClient.get<SignatureGroupDataResponse>(`/interfaces/signatures/${appName}/groupe`) as Promise<any>).catch(() => null),
            ])

            setUsers((usersData as People[]).map((u: People) => ({
                ...u,
                FullName: `${u.Lastname} ${u.Firstname.charAt(0).toUpperCase() + u.Firstname.slice(1).toLowerCase()}`
            })))

            if (groupData) {
                creditsHook.loadCredits(groupData.contrats || [])
                alertsHook.loadAlertes(
                    groupData.signature && groupData.signature.SeuilAlerte || 150,
                    groupData.alertes || []
                )
                relancesHook.loadRelances(groupData.signature && groupData.signature.Relances)
                const groupContreSignature = groupData.signature && groupData.signature.ContreSignatureAutomatique || false
                setContreSignatureGroupeState(groupContreSignature)

                if (groupData.connexionGroupe) {
                    const cx = groupData.connexionGroupe
                    setGroupConnexion({
                        IDSignatureParametreConnexion: cx.IDSignatureParametreConnexion,
                        IDPeopleRepresentant: cx.IDPeopleRepresentant,
                        Token: cx.Token,
                        OriginalToken: cx.Token,
                        IsTokenModified: false,
                        EstActif: cx.EstActif || false,
                        TestStatus: cx.EstActif ? 'active' : 'inactive',
                    })
                }

                if (groupData.societes && groupData.societes.length > 0) {
                    const societes: CompanyOverrideState[] = groupData.societes.map((co: any) => ({
                        IDPlaque: co.IDPlaque,
                        NomSociete: co.NomSociete,
                        CreditsConsommes: co.CreditsConsommes,
                        ContreSignatureAutomatique: co.ContreSignatureAutomatique,
                        sites: co.sites.map((s: any) => ({
                            IDSite: s.IDSite,
                            Descr: s.Descr,
                            IDSignatureParametreConnexion: s.IDSignatureParametreConnexion,
                            IDPeopleRepresentant: s.IDPeopleRepresentant,
                            Token: s.Token,
                            OriginalToken: s.Token,
                            IsTokenModified: false,
                            EstActif: s.EstActif || false,
                            TestStatus: (s.EstActif ? 'active' : 'inactive') as 'inactive' | 'active' | 'error',
                        }))
                    }))
                    const hasPerSite = societes.some(co => co.sites.some(s => s.IDPeopleRepresentant || s.Token))
                    setApplyConnectionToAll(!hasPerSite)
                    const hasPerCompanyCounterSign = societes.some(co => co.ContreSignatureAutomatique !== null)
                    // En mode par société, on fige la valeur des sociétés héritées (null) à partir du
                    // groupe afin qu'elles restent indépendantes du paramétrage groupe par la suite.
                    setCompanyOverrides(hasPerCompanyCounterSign
                        ? societes.map(co => ({
                            ...co,
                            ContreSignatureAutomatique: co.ContreSignatureAutomatique === null
                                ? groupContreSignature
                                : co.ContreSignatureAutomatique
                        }))
                        : societes
                    )
                    setApplyCounterSignToAll(!hasPerCompanyCounterSign)
                    return
                }
            }

            // Fallback : /groupe indisponible ou sans sociétés → charger depuis /societes/detailed
            const societesData: any[] = await (backofficeApiClient.get<any[]>(`/interfaces/signatures/${appName}/societes/detailed`) as Promise<any>).catch(() => [])
            setCompanyOverrides((societesData || []).map((societe: any) => ({
                IDPlaque: societe.IDPlaque,
                NomSociete: societe.NomSociete,
                CreditsConsommes: 0,
                ContreSignatureAutomatique: null,
                sites: (societe.Sites || []).map((site: any) => ({
                    IDSite: site.IDSite,
                    Descr: site.Descr,
                    IDSignatureParametreConnexion: null,
                    IDPeopleRepresentant: undefined,
                    Token: undefined,
                    OriginalToken: undefined,
                    IsTokenModified: false,
                    EstActif: false,
                    TestStatus: 'inactive' as 'inactive' | 'active' | 'error',
                }))
            })))
        } catch (err) {
            console.error('Erreur lors du chargement des données groupe:', err)
        } finally {
            setLoading(false)
        }
    }, [appName])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const handleUpdateGroupConnexion = (field: keyof GroupConnexionState, value: any) => {
        setGroupConnexion(prev => {
            const updated = { ...prev, [field]: value }
            if (field === 'Token') {
                const isModified = value !== prev.OriginalToken
                updated.IsTokenModified = isModified
                updated.TestStatus = isModified ? 'inactive' : (prev.EstActif ? 'active' : 'inactive')
            }
            return updated
        })
        setHasChanges(true)
    }

    const handleTestGroupConnexion = async () => {
        if (!groupConnexion.Token) {
            alert('Veuillez renseigner un token avant de tester')
            return
        }
        setTestingGroupConnexion(true)
        setGroupConnexion(prev => ({ ...prev, TestStatus: 'inactive' }))
        try {
            const result = await backofficeApiClient.post<any>(`/interfaces/signatures/${appName}/connexion/ping`, {
                token: groupConnexion.Token
            })
            setGroupConnexion(prev => ({ ...prev, TestStatus: result.success ? 'active' : 'error' }))
        } catch {
            setGroupConnexion(prev => ({ ...prev, TestStatus: 'error' }))
        } finally {
            setTestingGroupConnexion(false)
        }
    }

    const setContreSignatureGroupe = (v: boolean) => {
        setContreSignatureGroupeState(v)
        setHasChanges(true)
    }

    const handleToggleApplyConnectionToAll = () => {
        if (applyConnectionToAll) {
            // Passage en mode par site : activer les champs
            setApplyConnectionToAll(false)
        } else {
            // Retour en mode groupe : effacer les surcharges par site
            setCompanyOverrides(prev => prev.map(co => ({
                ...co,
                sites: co.sites.map(s => ({
                    ...s,
                    IDPeopleRepresentant: undefined,
                    Token: undefined,
                    OriginalToken: undefined,
                    IsTokenModified: false,
                    TestStatus: 'inactive' as const,
                    EstActif: false,
                }))
            })))
            setApplyConnectionToAll(true)
            setHasChanges(true)
        }
    }

    const handleToggleApplyCounterSignToAll = () => {
        if (applyCounterSignToAll) {
            // Passage en mode par société : figer la valeur de chaque société à partir du groupe
            // pour qu'elles deviennent indépendantes du paramétrage groupe.
            setCompanyOverrides(prev => prev.map(co => ({
                ...co,
                ContreSignatureAutomatique: co.ContreSignatureAutomatique === null
                    ? contreSignatureGroupe
                    : co.ContreSignatureAutomatique
            })))
            setApplyCounterSignToAll(false)
            setHasChanges(true)
        } else {
            // Retour en mode groupe : réinitialiser toutes les sociétés à null (héritage)
            setCompanyOverrides(prev => prev.map(co => ({
                ...co,
                ContreSignatureAutomatique: null
            })))
            setApplyCounterSignToAll(true)
            setHasChanges(true)
        }
    }

    const handleUpdateSiteOverride = (idPlaque: number, idSite: number, field: keyof SiteOverrideState, value: any) => {
        setCompanyOverrides(prev => prev.map(co => {
            if (co.IDPlaque !== idPlaque) return co
            return {
                ...co,
                sites: co.sites.map(s => {
                    if (s.IDSite !== idSite) return s
                    const updated = { ...s, [field]: value }
                    if (field === 'Token') {
                        const isModified = value !== s.OriginalToken
                        updated.IsTokenModified = isModified
                        updated.TestStatus = isModified ? 'inactive' : (s.EstActif ? 'active' : 'inactive')
                    }
                    return updated
                })
            }
        }))
        setHasChanges(true)
    }

    const handleTestSiteConnection = async (idPlaque: number, idSite: number) => {
        const site = companyOverrides.find(co => co.IDPlaque === idPlaque)?.sites.find(s => s.IDSite === idSite)
        if (!site?.Token) {
            alert('Veuillez renseigner un token avant de tester')
            return
        }
        const key = `${idPlaque}-${idSite}`
        setTestingSiteConnection(key)
        handleUpdateSiteOverride(idPlaque, idSite, 'TestStatus', 'inactive')
        try {
            const result = await backofficeApiClient.post<any>(`/interfaces/signatures/${appName}/connexion/ping`, {
                token: site.Token,
                siteId: idSite
            })
            handleUpdateSiteOverride(idPlaque, idSite, 'TestStatus', result.success ? 'active' : 'error')
        } catch {
            handleUpdateSiteOverride(idPlaque, idSite, 'TestStatus', 'error')
        } finally {
            setTestingSiteConnection(null)
        }
    }

    const handleUpdateCompanyCounterSign = (idPlaque: number, value: boolean | null) => {
        setCompanyOverrides(prev => prev.map(co =>
            co.IDPlaque === idPlaque ? { ...co, ContreSignatureAutomatique: value } : co
        ))
        setHasChanges(true)
    }

    const handleFilterCreditsByBdc = async (idPlaque: number, noCmd: string) => {
        if (!noCmd.trim()) {
            setCreditsByBdc(prev => {
                const next = { ...prev }
                delete next[idPlaque]
                return next
            })
            return
        }
        setCreditsByBdc(prev => ({ ...prev, [idPlaque]: { noCmd, creditsConsommes: null, loading: true } }))
        try {
            const result = await backofficeApiClient.get<{ creditsConsommes: number }>(
                `/interfaces/signatures/${appName}/groupe/societes/${idPlaque}/credits?noCmd=${encodeURIComponent(noCmd)}`
            )
            setCreditsByBdc(prev => ({ ...prev, [idPlaque]: { noCmd, creditsConsommes: result.creditsConsommes, loading: false } }))
        } catch {
            setCreditsByBdc(prev => ({ ...prev, [idPlaque]: { noCmd, creditsConsommes: null, loading: false } }))
        }
    }

    const getConnectionToggleClass = (status?: 'inactive' | 'active' | 'error') => {
        switch (status) {
            case 'active': return 'btn-success'
            case 'error': return 'btn-danger'
            default: return 'btn-secondary'
        }
    }

    const handleSave = async () => {
        if (creditsHook.credits.length === 0) {
            alert('Veuillez ajouter au moins un contrat de crédits')
            return
        }

        const data = {
            NomApp: appName,
            SeuilAlerte: alertsHook.seuilAlerte,
            Relances: stringifyRelances(relancesHook.relances),
            Status: 'ACTIVE',
            ContreSignatureAutomatique: contreSignatureGroupe,
            contrats: creditsHook.credits,
            deletedContrats: creditsHook.deletedContrats || [],
            connexionGroupe: (groupConnexion.IDPeopleRepresentant || groupConnexion.Token) ? {
                IDSignatureParametreConnexion: groupConnexion.IDSignatureParametreConnexion,
                IDPeopleRepresentant: groupConnexion.IDPeopleRepresentant,
                Token: groupConnexion.IsTokenModified ? groupConnexion.Token : undefined,
                EstActif: groupConnexion.EstActif,
            } : null,
            societes: companyOverrides.map(co => ({
                IDPlaque: co.IDPlaque,
                ContreSignatureAutomatique: co.ContreSignatureAutomatique,
                sites: co.sites.map(s => ({
                    IDSite: s.IDSite,
                    IDSignatureParametreConnexion: s.IDSignatureParametreConnexion,
                    IDPeopleRepresentant: s.IDPeopleRepresentant || null,
                    Token: s.IsTokenModified ? s.Token : undefined,
                    EstActif: s.EstActif,
                }))
            })),
            alertes: alertsHook.personnesAlerter,
            deletedAlertes: alertsHook.deletedAlertes || []
        }

        setSaving(true)
        try {
            await backofficeApiClient.post(`/interfaces/signatures/${appName}/groupe`, data)
            setHasChanges(false)
            window.location.reload()
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error.message || 'Erreur lors de l\'enregistrement'
            alert(errorMessage)
        } finally {
            setSaving(false)
        }
    }

    const handleCancel = () => {
        if (hasChanges) {
            setShowCancelModal(true)
        } else {
            history.push(`/interfaces/signatures/${appName}`)
        }
    }

    const confirmCancel = () => {
        setShowCancelModal(false)
        history.push(`/interfaces/signatures/${appName}`)
    }

    const handleDelete = async () => {
        try {
            const result = await backofficeApiClient.delete<{ archived: boolean }>(`/interfaces/signatures/${appName}/groupe`, {})
            setHasChanges(false)
            if (result && result.archived) {
                history.push(`/interfaces/signatures/${appName}`, { deletedArchived: true })
            } else {
                history.push(`/interfaces/signatures/${appName}`)
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error.message || 'Erreur lors de la suppression'
            alert(errorMessage)
        }
    }

    return {
        loading,
        saving,
        hasChanges,
        showCancelModal,
        setShowCancelModal,
        showDeleteModal,
        setShowDeleteModal,
        users,
        groupConnexion,
        handleUpdateGroupConnexion,
        handleTestGroupConnexion,
        testingGroupConnexion,
        contreSignatureGroupe,
        setContreSignatureGroupe,
        applyConnectionToAll,
        applyCounterSignToAll,
        handleToggleApplyConnectionToAll,
        handleToggleApplyCounterSignToAll,
        companyOverrides,
        handleUpdateSiteOverride,
        handleTestSiteConnection,
        testingSiteConnection,
        handleUpdateCompanyCounterSign,
        creditsByBdc,
        handleFilterCreditsByBdc,
        creditsHook,
        alertsHook,
        relancesHook,
        handleSave,
        handleCancel,
        confirmCancel,
        handleDelete,
        getConnectionToggleClass,
    }
}
